import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Cpu, Cable, Zap, Shield, Truck, CreditCard, Wrench, Star, Package } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-gp-gradient-light overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 -left-20 w-80 h-80 rounded-full bg-gp-gradient blur-3xl opacity-40" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#00D9A6] blur-3xl opacity-30" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-8 pb-20 sm:pt-10 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full text-xs font-quicksand font-bold text-[#1BB0CE] mb-6 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#00D9A6] animate-pulse" />
                LOJA OFICIAL GOPROTEGE
              </div>
              <h1 className="font-quicksand text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#3d3d3d] leading-[1.05]">
                Dispositivos para<br />
                <span className="text-[#00d9a6]">Rastreamento Veicular</span>
              </h1>
              <p className="text-base sm:text-lg text-[#666] mt-6 max-w-lg leading-relaxed">
                Rastreadores GPS, relés de bloqueio e chicotes prontos para instalação. Compre com tranquilidade e receba
                em todo o Brasil.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/loja" data-testid="hero-cta-shop" className="btn-gp">
                  VER CATÁLOGO <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/loja?cat=rastreador" data-testid="hero-cta-tracker" className="btn-gp-outline">
                  RASTREADORES
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-3 sm:gap-4 max-w-md sm:max-w-lg">
                {[
                  { k: "10K+", v: "unidades", icon: Package },
                  { k: "Envio", v: "Rápido", icon: Truck },
                  { k: "5/5", v: "Satisfação", icon: Star },
                ].map((s) => (
                  <div key={s.k} className="flex items-center gap-2 sm:gap-3 bg-white/60 backdrop-blur-sm p-3 rounded-2xl border border-[#f1f1f3] shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#1BB0CE]/10 flex items-center justify-center text-[#1BB0CE] shrink-0">
                      {s.icon && <s.icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />}
                    </div>
                    <div className="text-left leading-none">
                      <div className="font-quicksand text-sm sm:text-base font-extrabold text-[#3d3d3d]">{s.k}</div>
                      <div className="text-[10px] sm:text-xs text-[#666] font-medium mt-1">{s.v}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero image */}
            <div className="relative flex justify-center lg:justify-end">
              <img
                src="/tracker_woman.png"
                alt="Rastreador GoProtege"
                className="w-full max-w-[400px] lg:max-w-[560px] h-auto object-contain -mb-20 sm:-mb-28 relative z-10"
              />
            </div>


          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE] mb-3">
            CATEGORIAS
          </div>
          <h2 className="font-quicksand text-3xl sm:text-4xl font-extrabold text-[#3d3d3d]">
            Equipamentos por categoria
          </h2>
          <p className="text-sm text-[#666] mt-3 max-w-xl mx-auto">
            Tudo o que você precisa para instalar e manter um sistema de rastreamento veicular profissional.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Cpu, name: "Rastreadores", slug: "rastreador", desc: "Rastreadores GPS 4G LTE, OBD-II Plug & Play, estacionários, RFID." },
            { icon: Zap, name: "Relés", slug: "rele", desc: "Relés, terminais, conexão e bloqueadores." },
            { icon: Cable, name: "Chicotes", slug: "chicote", desc: "Chicotes para rastreadores, cabos e extensões pré-crimpadas." },
            { icon: Wrench, name: "Acessórios", slug: "acessorio", desc: "Fusíveis, fitas, ferramentas, conectores e outros." },
          ].map((c) => (
            <Link
              key={c.slug}
              to={`/loja?cat=${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="card-soft p-6 group block"
            >
              <div className="w-14 h-14 rounded-2xl bg-gp-gradient-light flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <c.icon className="w-7 h-7 text-[#1BB0CE]" strokeWidth={2} />
              </div>
              <h3 className="font-quicksand text-2xl font-bold text-[#3d3d3d]">{c.name}</h3>
              <p className="text-sm text-[#666] mt-2 leading-relaxed">{c.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 font-quicksand text-sm font-bold text-[#1BB0CE]">
                Explorar
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TRUST strip */}
      <section className="bg-gp-gradient-light py-16 border-y border-[#f1f1f3]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "QUALIDADE", desc: "Equipamentos testados em bancada, garantindo pleno funcionamento e confiabilidade." },
              { icon: Truck, title: "ENVIO RÁPIDO", desc: "Envio em até 24h para todo o Brasil." },
              { icon: CreditCard, title: "PAGAMENTO SEGURO", desc: "Aprovação automática e instantânea via PIX." },
            ].map((b) => (
              <div key={b.title} className="flex items-start gap-4 bg-white p-6 rounded-2xl border border-[#f1f1f3] shadow-sm">
                <div className="w-11 h-11 rounded-full bg-gp-gradient-light flex items-center justify-center flex-shrink-0">
                  <b.icon className="w-5 h-5 text-[#1BB0CE]" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="font-quicksand font-bold text-sm text-[#3d3d3d]">{b.title}</h4>
                  <p className="text-xs text-[#666] mt-1 leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest font-quicksand font-bold text-[#1BB0CE] mb-3">
                EM DESTAQUE
              </div>
              <h2 className="font-quicksand text-3xl sm:text-4xl font-extrabold text-[#3d3d3d]">
                Produtos mais vendidos
              </h2>
            </div>
            <Link to="/loja" className="btn-gp-outline" data-testid="featured-see-all">
              VER TODOS
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
